// src/components/AudioPlayer.jsx
import React, { useState, useRef, useEffect } from 'react';
import './AudioPlayer.css'; // This should point to your existing AudioPlayer.css

// Helper function to format time from seconds to MM:SS
const formatTime = (time) => {
  if (isNaN(time) || time === 0) return '00:00';
  const minutes = Math.floor(time / 60);
  const seconds = Math.floor(time % 60);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const AudioPlayer = ({ src, title }) => {
  const audioRef = useRef(null);
  const progressBarRef = useRef(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // This function will handle all event listener setup
  const setupAudioEvents = () => {
    const audio = audioRef.current;
    if (!audio) return; // Exit if audio element isn't ready

    const setAudioData = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
        if (progressBarRef.current) {
            progressBarRef.current.value = audio.currentTime;
            progressBarRef.current.style.setProperty('--progress-percentage', `${(audio.currentTime / audio.duration) * 100}%`);
        }
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', setAudioData);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);

    // Cleanup function to remove listeners
    return () => {
      audio.removeEventListener('loadedmetadata', setAudioData);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  };
  
  // Main useEffect hook to setup and teardown event listeners
  useEffect(setupAudioEvents, [src]); // Re-run if the src changes

  // Effect to handle play/pause from our button
  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play().catch(error => console.error("Error attempting to play audio:", error));
    } else {
      if(audioRef.current && !audioRef.current.paused) {
          audioRef.current.pause();
      }
    }
  }, [isPlaying]);

  // Effect to handle volume and mute changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const togglePlayPause = () => {
    setIsPlaying(prev => !prev);
  };

  const handleProgressChange = (e) => {
    if (audioRef.current) {
        audioRef.current.currentTime = e.target.value;
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
    }
  };
  
  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  // Render nothing if no audio source is provided. This prevents the component from rendering before it has a src.
  if (!src) {
      return null;
  }

  return (
    <div className="audio-player-wrapper">
      <audio ref={audioRef} src={src} preload="metadata"></audio>
      
      <div className="audio-player-ui">
        <button onClick={togglePlayPause} className="play-pause-btn" aria-label={isPlaying ? 'Pause' : 'Play'}>
          <i className={`fa-solid ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
        </button>

        <div className="audio-info-and-controls">
            <div className="audio-info">
                <span className="audio-status">{isPlaying ? 'Now playing' : 'Listen to a preview'}</span>
                <h4 className="audio-title">{title || 'Audio Version'}</h4>
            </div>

            <div className="main-controls">
                <span className="time-display current-time">{formatTime(currentTime)}</span>
                <input
                    type="range"
                    ref={progressBarRef}
                    className="progress-bar"
                    value={currentTime}
                    step="0.01"
                    min="0"
                    max={duration || 0}
                    onChange={handleProgressChange}
                    style={{'--progress-percentage': `${(currentTime / duration) * 100}%`}}
                />
                <span className="time-display duration">{formatTime(duration)}</span>
            </div>
        </div>

        <div className="volume-controls">
            <button onClick={toggleMute} className="volume-btn" aria-label={isMuted || volume === 0 ? 'Unmute' : 'Mute'}>
                <i className={`fa-solid ${isMuted || volume === 0 ? 'fa-volume-xmark' : volume < 0.5 ? 'fa-volume-low' : 'fa-volume-high'}`}></i>
            </button>
            <input
                type="range"
                className="volume-slider"
                value={isMuted ? 0 : volume}
                min="0"
                max="1"
                step="0.05"
                onChange={handleVolumeChange}
                style={{'--volume-percentage': `${isMuted ? 0 : volume * 100}%`}}
            />
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
